/**
 * The assistants' editor state (specs 022, 024): the panels' tabs, the
 * server's availability, and each conversation for this editor session
 * (022 D-3, 024 D-9: in memory only; reloading the page starts afresh).
 *
 * Conversations are keyed by target: a scene's by its bare id (022) and a
 * story's by `story:<id>` (`storyKey`).
 *
 * None of this is part of a document or its undo history. A scene turn sends
 * the current document; its steps are replayed with the scene store's
 * `applyOperations` (spec 021) as one undo step. A story turn saves first and
 * sends nothing but the message; its steps are applied with the story
 * store's `applyOperations` (spec 023) as one entry of the story history.
 */
import { create } from 'zustand';
import { apiClient, type AgentHistoryEntry, type AgentTurnStep, type MissingMediaItem, type StoryTurnPictures } from '../api/client';
import { replaySteps, type PlainChange, type SceneDocument } from '../../../Shared/src/operations';
import type { CreatedStoryScene } from '../../../Shared/src/storyOperations';
import type { SpriteSize, StageView } from '../../../Shared/src/@types';
import { loadImageNaturalSize } from '../utils/spriteGeometry';
import { pictureSizeOf, useMediaSheetStore } from './mediaSheetStore';
import { useSceneStore } from './sceneStore';
import { useStoryStore } from './storyStore';
import { currentAssistantView } from '../components/assistant/assistantView';

export type RightPanelTab = 'properties' | 'assistant';
/** The story editor's graph-tab left panel (spec 024 D-5). */
export type StoryPanelTab = 'scenes' | 'assistant';

/** The conversation key of a story (spec 024). */
export const storyKey = (storyId: number | string): string => `story:${storyId}`;
/**
 * The pending-brief key of a scene (spec 029 D-4): scene and story ids come
 * from different tables and collide, so a scene's brief is kept under
 * `scene:<id>` beside the stories' bare ids.
 */
export const sceneBriefKey = (sceneId: number | string): string => `scene:${sceneId}`;

export interface AssistantMessage {
  id: number;
  /** `notice`: a status line (Stopped, scene changed); `error`: a failed turn. Neither is resent. */
  role: 'user' | 'assistant' | 'notice' | 'error';
  text: string;
  /** The step summaries of an applied turn. */
  changes?: string[];
  /**
   * The applied turn's steps as the Changes report reads them (spec 028):
   * each step's summary and plain changes (`change` absent from an older
   * Service, which shows the summaries as a list).
   */
  changeSteps?: { summary: string; change?: PlainChange[] }[];
  /** Story turns (spec 028): the scenes the batch created, so a `new-` key opens its scene. */
  createdStoryScenes?: CreatedStoryScene[];
  /**
   * The scene object right after this turn was applied. The message's Undo
   * shows while the live scene is still this object, i.e. while the turn is
   * the most recent undo step.
   */
  appliedScene?: unknown;
  /**
   * The scene object right before this turn was applied. After one undo the
   * live scene is this object again, and the message offers Redo while the
   * redo stack's top is the turn.
   */
  beforeScene?: unknown;
  /** A failed line (502, 429, network, scene changed) that offers Retry of its turn's prompt. */
  retry?: boolean;
  /** Story turns (spec 024): the media the turn could not cast. */
  missingMedia?: MissingMediaItem[];
  /** Story turns: the id its batch entry carries in the story history (`StoryBatchEntry.turnId`). */
  turnId?: number;
  /** Story turns: a no-step reply on a story with no scenes offers "Build it now" (D-3). */
  buildNow?: boolean;
  /** Story turns (spec 026): the picture map's summary the turn returned. */
  pictures?: StoryTurnPictures;
  /** Spec 031: the turn's looks at the stage (shown as one muted line, never resent). */
  views?: StageView[];
  /** Story turns (spec 026 D-10) and applied scene turns (spec 029 D-13): the turn answered a pending brief from a start sheet. */
  fromBrief?: boolean;
}

export const HISTORY_LIMIT = 20;

/**
 * Spec 031 D-12: the muted line under a turn that looked at the stage. A
 * scene turn: "Looked at the stage"; a story turn: "Looked at the stage of "
 * and the distinct scene names, in order.
 */
export function lookedLine(views: readonly StageView[], story = false): string {
  if (!story) return 'Looked at the stage';
  const names = [...new Set(views.map((v) => v.sceneName))];
  return `Looked at the stage of ${names.join(', ')}`;
}

export const ASSISTANT_TEXT = {
  unavailable: "The assistant isn't set up on this server.",
  stopped: 'Stopped',
  sceneChanged: "The scene changed while I was working, so I didn't apply anything. Try again.",
  rateLimited: 'Too many requests in a minute. Wait a moment and try again.',
  failed: "The assistant couldn't finish that. Try again, or put it another way.",
  network: "Couldn't reach the assistant. Check your connection and try again.",
} as const;

export const STORY_ASSISTANT_TEXT = {
  saveFailed: "I couldn't save your latest changes, so I didn't start.",
  storyChanged: "The story changed while I was working, so I didn't apply anything. Try again.",
  preview: 'Stop the preview to edit with the assistant.',
  building: 'Building your story…',
  /** The fixed message "Build it now" sends (D-3). */
  buildNow: 'Please build it now, using your best assumptions and telling me what you assumed.',
} as const;

export const STORY_EXAMPLE_PROMPTS = [
  'Add a scene where the customer escalates to a manager',
  'Make the poor-choice branch shorter',
  'Tidy up the map',
] as const;

export const EXAMPLE_PROMPTS = [
  'Bob walks in from the left, then says hello',
  'Ask the learner how Bob should reply, with two choices',
  'Make every entrance a little slower',
] as const;

/** Media id → natural image size; replaceable in tests (jsdom decodes no images). */
type NaturalSizeLoader = (mediaId: number) => Promise<SpriteSize | undefined>;
const defaultLoader: NaturalSizeLoader = async (mediaId) => {
  try {
    return await loadImageNaturalSize(await apiClient.getMediaFileBlob(mediaId));
  } catch {
    return undefined;
  }
};
let naturalSizeLoader: NaturalSizeLoader = defaultLoader;
export function setNaturalSizeLoader(loader?: NaturalSizeLoader): void {
  naturalSizeLoader = loader ?? defaultLoader;
}
/** An image's natural size by the same loader the created sprites use (the studio's "Change picture", 025). */
export function loadNaturalSize(mediaId: number): Promise<SpriteSize | undefined> {
  return naturalSizeLoader(mediaId);
}

/**
 * The size a picture sizes a sprite by (spec 033): its spritesheet frame's
 * size when it has a cut (loaded with it), else its natural size. Picture
 * fills and the assistant's new sprites use it; `assignStateMedia` takes the
 * natural size and applies the cut itself.
 */
export async function loadPictureSize(mediaId: number): Promise<SpriteSize | undefined> {
  const [natural] = await Promise.all([naturalSizeLoader(mediaId), useMediaSheetStore.getState().loadSheets([mediaId])]);
  return natural ? pictureSizeOf(mediaId, natural) : undefined;
}

interface AssistantState {
  tab: RightPanelTab;
  /** The story editor's graph-tab left panel: Scenes or Assistant (editor UI state for the session). */
  storyPanelTab: StoryPanelTab;
  /**
   * Per story id: a brief from the start sheet, sent once as the story's
   * first turn (spec 024); per `scene:<id>` (`sceneBriefKey`), a scene's
   * brief from the scene start sheet (spec 029).
   */
  pendingBrief: Record<string, string>;
  status: 'unknown' | 'available' | 'unavailable';
  /** Per scene id. */
  conversations: Record<string, AssistantMessage[]>;
  /** Per scene id: a turn is in flight. */
  running: Record<string, boolean>;

  setTab: (tab: RightPanelTab) => void;
  loadStatus: () => Promise<void>;
  /** One scene turn (022); `fromBrief` (spec 029 D-13) marks an applied reply as the answer to a pending scene brief. */
  send: (sceneId: number | string, text: string, opts?: { fromBrief?: boolean }) => Promise<void>;
  stop: (sceneId: number | string) => void;
  /**
   * Retry a failed line: it and its turn's user bubble are removed and the
   * prompt is sent again as a new turn (so the bubble shows once, last).
   */
  retry: (sceneId: number | string, messageId: number) => Promise<void>;
  setStoryPanelTab: (tab: StoryPanelTab) => void;
  setPendingBrief: (storyId: number | string, brief: string) => void;
  /** The story's pending brief, cleared as it is read (so a reload or remount never sends it twice). */
  takePendingBrief: (storyId: number | string) => string | undefined;
  /** Spec 029: a scene's brief from the scene start sheet, under `sceneBriefKey(id)`. */
  setPendingSceneBrief: (sceneId: number | string, brief: string) => void;
  /** The scene's pending brief, cleared as it is read; never a story's entry. */
  takePendingSceneBrief: (sceneId: number | string) => string | undefined;
  /**
   * One story turn (spec 024): saves the story and a dirty open scene, posts
   * the turn, and applies its steps with `storyStore.applyOperations` as one
   * history entry. Conversation `story:<id>`.
   */
  sendStory: (storyId: number, text: string, opts?: { selectedSceneId?: number; fromBrief?: boolean }) => Promise<void>;
  /** Retry a failed story line, as `retry` does for a scene. */
  retryStory: (storyId: number, messageId: number, opts?: { selectedSceneId?: number }) => Promise<void>;
  /** Push a status line into a conversation (a failed undo or redo of a turn's batch). */
  pushNotice: (key: string, text: string) => void;
  /** Tests: forget everything. */
  reset: () => void;
}

const controllers = new Map<string, AbortController>();
let nextId = 1;
let nextTurnId = 1;
let statusRequest: Promise<void> | null = null;

/**
 * The scene store's document: `scene` plus its authored keyframes (spec 021
 * D-1, 022 v21, 036): the store's map as `keyframesToArray` gives it (one
 * track per non-empty entry, written out here because tests mock the store
 * module), so the Service's working copy, its summary, `getSprite` and
 * `viewStage` see the authored keys. The store's scene never holds a
 * retired composer mode (`loadScene` strips it), so the posted document carries none.
 */
function liveDocument(): SceneDocument {
  const { scene, keyframes } = useSceneStore.getState();
  const tracks: { trackId: string; keyframes: unknown[] }[] = [];
  keyframes?.forEach((kfs, trackId) => { if (kfs.length > 0) tracks.push({ trackId, keyframes: kfs }); });
  return { ...scene, keyframes: tracks } as unknown as SceneDocument;
}

/**
 * The natural sizes of the pictures of every sprite left without a box by a
 * dry run of the steps, by media id: the replay's `imageSizes`, from which
 * the operations size a sprite they create or re-picture (spec 021 D-19).
 */
async function naturalSizesFor(doc: SceneDocument, steps: AgentTurnStep[]): Promise<Map<number, SpriteSize>> {
  const sizes = new Map<number, SpriteSize>();
  const dry = replaySteps(doc, steps);
  if (dry.ok === false) return sizes;
  const mediaIds = new Set<number>();
  for (const sprite of dry.doc.sprites) {
    if (sprite.size) continue;
    for (const state of sprite.states) if (state.mediaId) mediaIds.add(state.mediaId);
  }
  await Promise.all([...mediaIds].map(async (id) => {
    const size = await loadPictureSize(id);
    if (size) sizes.set(id, size);
  }));
  return sizes;
}

/**
 * The lengths (ms) of the voices a replay of the steps can fit (spec 041), by
 * media id: the replay's `mediaLengths`, so a voiced line is fitted here
 * exactly as the Service fitted it with its media map. A `setDialog` refits
 * every voiced, unset line whether or not it names the voice (D-9), so these
 * are the voices of the live document's dialog lines plus those the steps'
 * `setDialog` lines name. Read with each voice's metadata
 * (`mediaSheetStore`); an unreadable one is left out (an unknown length).
 */
export async function voiceLengthsFor(doc: SceneDocument, steps: readonly { name: string; args: unknown }[]): Promise<Map<number, number>> {
  const ids = new Set<number>();
  const add = (voice: unknown) => {
    if (typeof voice === 'number' && Number.isInteger(voice) && voice > 0) ids.add(voice);
  };
  for (const dialog of doc.dialogs ?? []) for (const line of dialog.lines ?? []) add(line.audioId);
  for (const step of steps) {
    if (step.name !== 'setDialog') continue;
    const lines = (step.args as { lines?: unknown } | null)?.lines;
    if (!Array.isArray(lines)) continue;
    for (const line of lines) add((line as { voice?: unknown } | null)?.voice);
  }
  const lengths = new Map<number, number>();
  if (!ids.size) return lengths;
  await useMediaSheetStore.getState().loadSheets(ids);
  const known = useMediaSheetStore.getState().lengths;
  for (const id of ids) if (known[id] !== undefined) lengths.set(id, known[id]);
  return lengths;
}

/** The scenes the batch of turn `turnId` created (spec 028), read from its entry in the story history. */
function createdScenesOf(turnId: number): CreatedStoryScene[] {
  const { past } = useStoryStore.getState().history;
  for (let i = past.length - 1; i >= 0; i--) {
    const entry = past[i] as { kind?: string; turnId?: number; createdStoryScenes?: CreatedStoryScene[] };
    if (entry.kind === 'batch' && entry.turnId === turnId) return entry.createdStoryScenes ?? [];
  }
  return [];
}

/** The conversation's text turns, most recent 20, as the request's `history`. */
export function historyOf(messages: readonly AssistantMessage[]): AgentHistoryEntry[] {
  return messages
    .filter((m): m is AssistantMessage & { role: 'user' | 'assistant' } => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({ role: m.role, text: m.text }))
    .slice(-HISTORY_LIMIT);
}

const errorText = (error: string | undefined): string => {
  if (error === 'agent_rate_limited') return ASSISTANT_TEXT.rateLimited;
  if (error === 'agent_unavailable') return ASSISTANT_TEXT.unavailable;
  return ASSISTANT_TEXT.failed;
};

export const useAssistantStore = create<AssistantState>()((set, get) => {
  const push = (key: string, message: Omit<AssistantMessage, 'id'>) =>
    set((s) => ({ conversations: { ...s.conversations, [key]: [...(s.conversations[key] ?? []), { id: nextId++, ...message }] } }));

  const takeBrief = (key: string): string | undefined => {
    const brief = get().pendingBrief[key];
    if (brief === undefined) return undefined;
    set((s) => {
      const { [key]: _taken, ...rest } = s.pendingBrief;
      return { pendingBrief: rest };
    });
    return brief;
  };

  return {
    tab: 'properties',
    storyPanelTab: 'scenes',
    pendingBrief: {},
    status: 'unknown',
    conversations: {},
    running: {},

    setTab: (tab) => set({ tab }),
    setStoryPanelTab: (storyPanelTab) => set({ storyPanelTab }),
    setPendingBrief: (storyId, brief) => set((s) => ({ pendingBrief: { ...s.pendingBrief, [String(storyId)]: brief } })),
    takePendingBrief: (storyId) => takeBrief(String(storyId)),
    setPendingSceneBrief: (sceneId, brief) => set((s) => ({ pendingBrief: { ...s.pendingBrief, [sceneBriefKey(sceneId)]: brief } })),
    takePendingSceneBrief: (sceneId) => takeBrief(sceneBriefKey(sceneId)),
    pushNotice: (key, text) => push(key, { role: 'notice', text }),

    sendStory: async (storyId, rawText, opts = {}) => {
      const key = storyKey(storyId);
      const text = rawText.trim();
      const story = useStoryStore.getState().story;
      if (!text || get().running[key] || !story || story.id !== storyId) return;

      const history = historyOf(get().conversations[key] ?? []);
      push(key, { role: 'user', text });
      const controller = new AbortController();
      controllers.set(key, controller);
      set((s) => ({ running: { ...s.running, [key]: true } }));
      const fail = (line: string) => push(key, { role: 'error', text: line, retry: true });
      const stopped = () => push(key, { role: 'notice', text: ASSISTANT_TEXT.stopped });

      try {
        // The Service reads the saved story: the author's latest edits go first.
        const saved = await useStoryStore.getState().saveStory();
        const sc = useSceneStore.getState();
        let sceneSaved = true;
        if (sc.scene && sc.isDirty) {
          await sc.saveScene();
          sceneSaved = useSceneStore.getState().saveStatus !== 'error';
        }
        if (controller.signal.aborted) return stopped();
        if (!saved || !sceneSaved) return fail(STORY_ASSISTANT_TEXT.saveFailed);

        // Spec 031 v3: what the author is looking at, read as the turn is posted.
        const view = currentAssistantView();
        const res = await apiClient.storyAgentTurn(storyId, {
          message: text,
          history,
          ...(opts.selectedSceneId !== undefined ? { selectedSceneId: opts.selectedSceneId } : {}),
          ...(view ? { view } : {}),
        }, { signal: controller.signal });
        if (controller.signal.aborted) return stopped();
        if (!res.success) return fail(errorText(res.error));

        const { reply, steps, missingMedia = [], pictures, views = [] } = res.data;
        const media = {
          ...(missingMedia.length ? { missingMedia } : {}),
          ...(pictures ? { pictures } : {}),
          ...(views.length ? { views } : {}),
          ...(opts.fromBrief ? { fromBrief: true } : {}),
        };
        if (steps.length === 0) {
          const empty = (useStoryStore.getState().story?.storyScenes.length ?? 0) === 0;
          return push(key, { role: 'assistant', text: reply, ...media, ...(empty ? { buildNow: true } : {}) });
        }
        if (useStoryStore.getState().story?.id !== storyId) {
          return push(key, { role: 'notice', text: STORY_ASSISTANT_TEXT.storyChanged, retry: true });
        }
        const turnId = nextTurnId++;
        const applied = await useStoryStore.getState().applyOperations(steps, { turnId });
        if (!applied.ok) {
          return applied.reason === 'network'
            ? fail(ASSISTANT_TEXT.network)
            : push(key, { role: 'notice', text: STORY_ASSISTANT_TEXT.storyChanged, retry: true });
        }
        return push(key, {
          role: 'assistant',
          text: reply,
          changes: steps.map((s) => s.summary),
          changeSteps: steps.map((s) => ({ summary: s.summary, ...(s.change ? { change: s.change } : {}) })),
          createdStoryScenes: createdScenesOf(turnId),
          turnId,
          ...media,
        });
      } catch {
        return controller.signal.aborted ? stopped() : fail(ASSISTANT_TEXT.network);
      } finally {
        controllers.delete(key);
        set((s) => ({ running: { ...s.running, [key]: false } }));
      }
    },

    retryStory: (storyId, messageId, opts) => {
      const key = storyKey(storyId);
      const messages = get().conversations[key] ?? [];
      const at = messages.findIndex((m) => m.id === messageId);
      if (at < 0 || !messages[at].retry || get().running[key]) return Promise.resolve();
      let prompt = -1;
      for (let i = at - 1; i >= 0; i--) if (messages[i].role === 'user') { prompt = i; break; }
      if (prompt < 0) return Promise.resolve();
      const text = messages[prompt].text;
      set((s) => ({ conversations: { ...s.conversations, [key]: messages.filter((_, i) => i !== at && i !== prompt) } }));
      return get().sendStory(storyId, text, opts);
    },

    loadStatus: () => {
      if (get().status !== 'unknown') return Promise.resolve();
      if (!statusRequest) {
        statusRequest = apiClient.getAgentStatus()
          .then((res) => set({ status: res.success && res.data.available ? 'available' : 'unavailable' }))
          .catch(() => set({ status: 'unavailable' }))
          .finally(() => { statusRequest = null; });
      }
      return statusRequest;
    },

    send: async (sceneId, rawText, opts = {}) => {
      const key = String(sceneId);
      const text = rawText.trim();
      const scene = useSceneStore.getState().scene;
      if (!text || get().running[key] || !scene || String(scene.id) !== key) return;

      const history = historyOf(get().conversations[key] ?? []);
      push(key, { role: 'user', text });
      const fail = (text: string) => push(key, { role: 'error', text, retry: true });
      const sceneChanged = () => push(key, { role: 'notice', text: ASSISTANT_TEXT.sceneChanged, retry: true });
      const controller = new AbortController();
      controllers.set(key, controller);
      set((s) => ({ running: { ...s.running, [key]: true } }));
      const finish = () => {
        controllers.delete(key);
        set((s) => ({ running: { ...s.running, [key]: false } }));
      };

      // Spec 022 v21: the posted document is the scene with its authored keyframes (021's SceneDocument).
      const doc = liveDocument();
      try {
        // Spec 031 v3: what the author is looking at, at the moment of Send.
        const view = currentAssistantView();
        const res = await apiClient.agentTurn(sceneId, { message: text, history, document: doc, ...(view ? { view } : {}) }, { signal: controller.signal });
        if (controller.signal.aborted) return push(key, { role: 'notice', text: ASSISTANT_TEXT.stopped });
        if (!res.success) return fail(errorText(res.error));

        const { reply, steps, pictures, views = [] } = res.data;
        const looked = views.length ? { views } : {};
        if (steps.length === 0) return push(key, { role: 'assistant', text: reply, ...looked });

        const live = useSceneStore.getState().scene;
        const [naturalSizes, mediaLengths] = live && String(live.id) === key
          ? await Promise.all([naturalSizesFor(liveDocument(), steps), voiceLengthsFor(liveDocument(), steps)])
          : [new Map<number, SpriteSize>(), new Map<number, number>()];
        if (controller.signal.aborted) return push(key, { role: 'notice', text: ASSISTANT_TEXT.stopped });

        // Another scene took the store (a story tab switch), or a step no longer applies.
        const before = useSceneStore.getState().scene;
        if (!before || String(before.id) !== key) return sceneChanged();
        const applied = useSceneStore.getState().applyOperations(steps, { naturalSizes, mediaLengths });
        if (!applied.ok) return sceneChanged();
        const after = useSceneStore.getState().scene;
        return push(key, {
          role: 'assistant',
          text: reply,
          changes: steps.map((s) => s.summary),
          changeSteps: steps.map((s) => ({ summary: s.summary, ...(s.change ? { change: s.change } : {}) })),
          ...(after !== before ? { appliedScene: after, beforeScene: before } : {}),
          // Spec 030: the scene's own picture map after the turn.
          ...(pictures ? { pictures } : {}),
          ...looked,
          ...(opts.fromBrief ? { fromBrief: true } : {}),
        });
      } catch {
        return controller.signal.aborted
          ? push(key, { role: 'notice', text: ASSISTANT_TEXT.stopped })
          : fail(ASSISTANT_TEXT.network);
      } finally {
        finish();
      }
    },

    stop: (sceneId) => {
      controllers.get(String(sceneId))?.abort();
    },

    retry: (sceneId, messageId) => {
      const key = String(sceneId);
      const messages = get().conversations[key] ?? [];
      const at = messages.findIndex((m) => m.id === messageId);
      const scene = useSceneStore.getState().scene;
      if (!scene || String(scene.id) !== key) return Promise.resolve();
      if (at < 0 || !messages[at].retry || get().running[key]) return Promise.resolve();
      let prompt = -1;
      for (let i = at - 1; i >= 0; i--) if (messages[i].role === 'user') { prompt = i; break; }
      if (prompt < 0) return Promise.resolve();
      const text = messages[prompt].text;
      set((s) => ({ conversations: { ...s.conversations, [key]: messages.filter((_, i) => i !== at && i !== prompt) } }));
      return get().send(sceneId, text);
    },

    reset: () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
      statusRequest = null;
      set({ tab: 'properties', storyPanelTab: 'scenes', pendingBrief: {}, status: 'unknown', conversations: {}, running: {} });
    },
  };
});
